const selectorsVideo = {
  VIDEO_THUMBNAIL_SELECTOR: '.js-video-player-thumbnail',
  VIDEO_THUMBNAIL_BUTTON_SELECTOR: '.js-video-player-thumbnail-button',
  MUTE_BUTTON_SELECTOR: '.js-mute-button',
  UNMUTE_BUTTON_SELECTOR: '.js-unmute-button',
  HIDDEN_CLASS: 'opacity-0',
  PLAYING_CLASS: 'is-playing',
  LOADING_CLASS: 'is-loading',
  PAUSING_CLASS: 'is-pausing',
  VIDEO_TYPES: {
    YOUTUBE: 'youtube',
    VIMEO: 'vimeo',
    MP4: 'mp4'
  },
  STATES: {
    PLAYING: 'playing',
    PAUSING: 'pausing',
    LOADING: 'loading'
  },
  DEBOUNCE_TIME: 200,
  INTERVAL_TIME: 200,
  INTERVAL_TIME_LOADING: 300,
  LIMIT_THRESHOLD: 30
}

const debounceFunc = (func, timeout = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

const loadScript = (src) => {
  const script = document.createElement('script');

  script.src = src;
  script.type = 'text/javascript';
  script.defer = true;
  document.body.appendChild(script);
}

class VideoPlayer extends HTMLElement {
  constructor() {
    super()
    this.player = null
    this.element = document.createElement('div')
    this.type = this.getAttribute('data-video-type') || selectorsVideo.VIDEO_TYPES.MP4
    this.thumbnailEl = document.querySelector(selectorsVideo.VIDEO_THUMBNAIL_SELECTOR) || null
    this.isLoaded = false
    this.src = this.getAttribute('src') || null
    this.playOnClick = false
    this.status = {
      play: null,
      error: null
    }
    this.options = {
      autoplay: this.hasAttribute('autoplay'),
      controls: this.hasAttribute('controls'),
      playsinline: this.hasAttribute('playsinline'),
      muted: this.hasAttribute('muted'),
      loop: this.hasAttribute('loop'),
      origin: window.location.origin,
      poster: this.getAttribute('data-poster'),
      thumbnail: this.getAttribute('data-thumbnail'),
      playIntersection: this.hasAttribute('data-play-intersection'),
      lazyload: this.hasAttribute('lazyload'),
      initialThumbnail: this.hasAttribute('initial-thumbnail')
    }

    if (this.type === selectorsVideo.VIDEO_TYPES.YOUTUBE) {
      this.appendChild(this.element)
    }

    if (!this.src) {
      this.status.error = 'No video src'
    }

    if (this.options.playIntersection) {
      this.initWhenVisible()
    }
    this.unload = this.unload.bind(this)
    this.play = this.play.bind(this)
    this.pause = this.pause.bind(this)
    this.initWhenVisible = this.initWhenVisible.bind(this)

    !this.thumbnailEl && this.options.thumbnail && this.initThumbnail()

    if (this.hasAttribute('auto-init')) {
      this.init()
    }

    if (this.options.lazyload) {
      this.lazyload()
    }
  }

  get getVideoID() {
    if (this.getAttribute('data-external-id')) return this.getAttribute('data-external-id')

    if (/youtu\.?be/.test(this.src)) {
      const patterns = [
        /youtu\.be\/([^#&?]{11})/, // youtu.be/<id>
        /\?v=([^#&?]{11})/, // ?v=<id>
        /&v=([^#&?]{11})/, // &v=<id>
        /embed\/([^#&?]{11})/, // embed/<id>
        /\/v\/([^#&?]{11})/ // /v/<id>
      ]

      for (let i = 0; i < patterns.length; ++i) {
        if (patterns[i].test(this.src)) {
          return patterns[i].exec(this.src)[1]
        }
      }
    }

    if (/^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.test(this.src)) {
      const parseURL = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.exec(this.src)

      return parseURL[5]
    }

    return null
  }

  initWhenVisible() {
    this.observeVisibility(this).then((isVisible) => {
      if (!this.isLoaded && isVisible) {
        this.src && this.type && this.init()
        this.isLoaded = true
      }
    })
  }

  unload() {
    switch (this.type) {
      case 'youtube':
        this.player.clearVideo()
        break
      case 'vimeo':
        this.player.unload()
        break
    }
  }

  initThumbnail() {
    const thumbnailImage = this.options.thumbnail
      ? `<img src="${this.options.thumbnail}" loading="lazy" alt="thumbnail video" />`
      : ''

    const html = `
      <div class="js-video-player-thumbnail hero__video-thumbnail">
          ${thumbnailImage}
          <button type="button" class="js-video-player-thumbnail-button hero__video-button">
            <div class="hero__video--state hero__video--pause">
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" fill="none" viewBox="0 0 96 96" aria-hidden="true" focusable="false">
                <path d="M48 84c19.882 0 36-16.118 36-36S67.882 12 48 12 12 28.118 12 48s16.118 36 36 36Z" stroke="#FFFFFF" stroke-width="3" stroke-miterlimit="10"/>
                <path d="M60 48 42 36v24l18-12Z" fill="#FFFFFF" stroke="#FFFFFF" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="hero__video--state hero__video--play">
              <svg width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
                <g stroke="#FFFFFF" stroke-linecap="round" stroke-linejoin="round"><path d="M25 5h-4.5a1 1 0 0 0-1 1v20a1 1 0 0 0 1 1H25a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1ZM11.5 5H7a1 1 0 0 0-1 1v20a1 1 0 0 0 1 1h4.5a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1Z" fill="#FFFFFF"/>
                </g>
              </svg>
            </div>
            <div class="hero__video--state hero__video--loading"></div>
          </button>
        </div>
      </div>
      `

    this.thumbnailEl = html
    this.insertAdjacentHTML('beforeend', this.thumbnailEl)

    const buttonEl = this.querySelector(selectorsVideo.VIDEO_THUMBNAIL_BUTTON_SELECTOR)

    if (this.options.autoplay) {
      this.setStatePlayer(selectorsVideo.STATES.LOADING)
    }

    buttonEl.addEventListener('click', () => {
      if (!this.player) {
        this.setStatePlayer(selectorsVideo.STATES.LOADING)
        this.playOnClick = true

        if (this.type === selectorsVideo.VIDEO_TYPES.YOUTUBE) {
          this.setupYTPlayer()
        }

        if (this.type === selectorsVideo.VIDEO_TYPES.VIMEO) {
          this.setupVimeoPlayer()
        }

        if (this.type === selectorsVideo.VIDEO_TYPES.MP4) {
          this.setupMP4Player()
        }
      } else {
        if (!this.status.play) {
          this.play()
        } else {
          this.pause()
        }
      }
    })
  }

  initYoutubePlayer() {
    let count = 0

    if (this.options.autoplay && this.options.initialThumbnail) {
      this.setStatePlayer(selectorsVideo.STATES.LOADING)
    }

    const intervalVideo = setInterval(() => {
      count++

      if (count > selectorsVideo.LIMIT_THRESHOLD) {
        clearInterval(intervalVideo)
      }

      if (window.YT) {
        YT.ready(() => this.setupYTPlayer())
        clearInterval(intervalVideo)
      }
    }, selectorsVideo.INTERVAL_TIME);
  }

  initVimeoPlayer() {
    let count = 0

    if (this.options.autoplay && this.options.initialThumbnail) {
      this.setStatePlayer(selectorsVideo.STATES.LOADING)
    }

    const intervalVideo = setInterval(() => {
      count++

      if (count > selectorsVideo.LIMIT_THRESHOLD) {
        clearInterval(intervalVideo)
      }

      if (window.Vimeo) {
        this.setupVimeoPlayer()
        clearInterval(intervalVideo)
      }
    }, selectorsVideo.INTERVAL_TIME);
  }

  initMP4Player() {
    if (this.querySelector('video')) return
    
    if (this.options.autoplay && this.options.initialThumbnail) {
      this.setStatePlayer(selectorsVideo.STATES.LOADING)
    }

    this.setupMP4Player()
  }

  async initByType(type) {
    switch (type) {
      case selectorsVideo.VIDEO_TYPES.YOUTUBE:
        this.initYoutubePlayer()
        break
      case selectorsVideo.VIDEO_TYPES.VIMEO:
        this.initVimeoPlayer()
        break
      case selectorsVideo.VIDEO_TYPES.MP4:
        this.initMP4Player()
        break
    }
  }

  async loadPlayerAPIs(type) {
    let src = ''

    if (type === selectorsVideo.VIDEO_TYPES.YOUTUBE && !window.YT) {
      src = 'https://www.youtube.com/iframe_api'
    } else if (type === selectorsVideo.VIDEO_TYPES.VIMEO && !window.Vimeo) {
      src = 'https://player.vimeo.com/api/player.js'
    } else {
      return
    }

    return loadScript(src)
  }

  setupYTPlayer() {
    const options = {
      videoId: this.getVideoID,
      playerVars: {
        autoplay: 0,
        controls: this.options.controls ? 1 : 0,
        height: '100%',
        width: '100%',
        playsinline: this.options.playsinline ? 1 : 0,
        loop: this.options.loop ? 1 : 0
      },
      events: {
        onError: () => {
          this.status.error = new Error('player could not be built')
        },
        onStateChange: (e) => this._onYouTubeStateChange(e),
        onReady: (e) => this._onYoutubeReadyState(e)
      }
    }

    if (YT.loaded) {
      this.player = new window.YT.Player(this.element, options)
      this.videoMuteToggle(this.player)
    }
  }

  setupVimeoPlayer() {
    if (this.player) return

    const options = {
      id: this.getVideoID,
      ...this.options
    }

    this.player = new window.Vimeo.Player(this, options)

    if (!this.options.autoplay) {
      this.player.ready().then(() => {
        this.onPlay()
        this.setStatePlayer(selectorsVideo.STATES.PLAYING)
      })
    } else {
      this.player.ready().then(() => {
        this.onPlay()
      })
    }

    this.player.on('play', async (data) => {
      const isPaused = await this.player.getPaused()
      const isEnded = await this.player.getEnded()
      const currentTime = data?.seconds || 0

      if ((!isPaused && !isEnded && currentTime > 0 && this.options.controls) || !this.options.controls) {
        this.onPlay()
      }
    })
    this.player.on('pause', () => this.onPause())
    this.player.on('ended', () => this.onEnd)
    this.videoMuteToggle(this.player)
  }

  setupMP4Player() {
    this.videoEl = document.createElement('video')
    this.videoEl.src = this.src

    this.player = this.videoEl
    this.appendChild(this.videoEl)

    if (!this.options.autoplay) {
      this.onPlay()
      this.setStatePlayer(selectorsVideo.STATES.PLAYING)
    }

    this.player.controls = this.options.controls
    this.player.autoplay = this.options.autoplay
    this.player.playsInline = this.options.playsinline
    this.player.muted = this.options.muted
    this.player.loop = this.options.loop
    this.player.poster = this.options.poster || ''

    if (this.options.autoplay) {
      this.videoEl.setAttribute('autoplay', 'autoplay')
      this.videoEl.setAttribute('muted', 'muted')
      this.videoEl.setAttribute('playsinline', 'playsinline')
    }
    if (this.options.loop) {
      this.videoEl.setAttribute('loop', 'loop')
    }

    this.player.addEventListener('play', () => {
      if (
        (!this.videoEl.paused && this.videoEl.currentTime > 0 && !this.videoEl.ended && this.options.controls) ||
        !this.options.controls
      ) {
        this.onPlay()
      }
    })

    if (!this.player.autoplay) {
      this.player.addEventListener('pause', () => this.onPause())
      this.player.addEventListener('ended', () => this.onEnd())
    }

    if (this.player.autoplay) {
      const restartVideo = (forceRestart) => {
        if (this.player.paused || this.player.readyState < 3) {
          this.player.play()
        }

        if (forceRestart) {
          this.player.load()
          this.player.play()
        }
      }

      this.player.addEventListener("pause", () => restartVideo())
      this.player.addEventListener("ended", () => restartVideo())
      this.player.addEventListener("error", () => restartVideo(true))

      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) {
          restartVideo(true)
        }
      })
    }
    this.videoMuteToggle(this.player)
  }

  play() {
    try {
      if (!this.player) return

      if (this.type === selectorsVideo.VIDEO_TYPES.YOUTUBE) {
        this.player.playVideo()
      } else {
        this.player.play()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  pause() {
    try {
      if (!this.player) return

      if (this.type === selectorsVideo.VIDEO_TYPES.YOUTUBE) {
        this.player.pauseVideo()
      } else {
        this.player.pause()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  onPlay() {
    this.play()
    this.status.play = true
    this.dispatchEvent(new CustomEvent('play'))
    this.setStatePlayer(selectorsVideo.STATES.PLAYING)
    this.displayThumbnail(false)
    setTimeout(() => {
      this.displayLoadingAnimation()
    }, 500);
  }

  onPause() {
    this.pause()
    this.status.play = false
    this.dispatchEvent(new CustomEvent('pause'))
    this.setStatePlayer(selectorsVideo.STATES.PAUSING)
    this.displayThumbnail(false)
  }

  onEnd() {
    if (this.options.loop) {
      this.play()
    } else {
      this.status.play = false
      this.dispatchEvent(new CustomEvent('ended'))
      this.displayThumbnail(true)
      this.setStatePlayer(null)
    }
  }

  setStatePlayer(status) {
    switch (status) {
      case selectorsVideo.STATES.PLAYING:
        this.classList.add(selectorsVideo.PLAYING_CLASS)
        this.classList.remove(selectorsVideo.PAUSING_CLASS)
        this.classList.remove(selectorsVideo.LOADING_CLASS)
        break
      case selectorsVideo.STATES.PAUSING:
        this.classList.add(selectorsVideo.PAUSING_CLASS)
        this.classList.remove(selectorsVideo.PLAYING_CLASS)
        this.classList.remove(selectorsVideo.LOADING_CLASS)
        break
      case selectorsVideo.STATES.LOADING:
        this.classList.add(selectorsVideo.LOADING_CLASS)
        this.classList.remove(selectorsVideo.PLAYING_CLASS)
        this.classList.remove(selectorsVideo.PAUSING_CLASS)
        break
      default:
        this.classList.remove(selectorsVideo.LOADING_CLASS)
        this.classList.remove(selectorsVideo.PLAYING_CLASS)
        this.classList.remove(selectorsVideo.PAUSING_CLASS)
    }
  }

  displayThumbnail(status) {
    const el = this.querySelector(selectorsVideo.VIDEO_THUMBNAIL_SELECTOR)

    if (!el) return

    if (status) {
      el.classList.remove(selectorsVideo.HIDDEN_CLASS)
    } else {
      el.classList.add(selectorsVideo.HIDDEN_CLASS)
    }
  }

  displayLoadingAnimation() {
    const logoLoadingEl = document.querySelector('.page-loading')
    const animationEls = document.querySelectorAll('.js-animation-loading')

    if (logoLoadingEl) {
      logoLoadingEl.classList.add('animation-fade-out')
    }
    if (animationEls.length > 0) {
      animationEls.forEach((item) => {
        item.classList.add('animation-fade-in')
        item.classList.remove('animation-fade-out')
      })
    }
  }

  _onYouTubeStateChange(e) {
    const stateMap = {
      '-1': 'unstarted',
      0: 'ended',
      1: 'playing',
      2: 'pause',
      3: 'buffering',
      5: 'cued'
    }
    const state = stateMap[e.data.toString()]
    const eventMethodMap = {
      ended: this.onEnd,
      pause: this.onPause,
      playing: this.onPlay
    }

    if (eventMethodMap[state]) {
      const method = eventMethodMap[state]

      if (method) {
        method.call(this)
      }
    }
  }

  _onYoutubeReadyState() {
    if (this.options.autoplay || this.playOnClick) {
      this.setStatePlayer(selectorsVideo.STATES.PLAYING)
      this.play()
    }

    if (this.options.muted && this.player?.mute) {
      this.player.mute()
    }
  }

  observeVisibility(element) {
    return new Promise((resolve) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting

          resolve(isVisible)
        })
      })

      observer.observe(element)
    })
  }

  lazyload() {
    const observer = new IntersectionObserver(
      debounceFunc((entries) => {
        entries.forEach((entry) => {
          if (this.options.lazyload) {
            if (entry.isIntersecting) {
              if (!this.isLoaded) {
                this.src && this.type && this.init()
                this.isLoaded = true
              } else if (this.options.autoplay) {
                this.player && this.onPlay()
              }
            }
          }
        })
      }, selectorsVideo.DEBOUNCE_TIME)
    )

    observer.observe(this)
  }

  videoToggle() {
    const html =
      !this.thumbnailEl && !this.options.thumbnail
        ? `<div class="absolute left-0 top-0 w-full h-full z-5 flex justify-center items-center cursor-pointer js-video-toggle">
            <div class="rounded-full text-while absolute h-12 w-12 bg-primary flex z-50 items-center justify-center [.is-loading_&]:hidden [.is-pausing_&]:hidden [.is-playing_&]:hidden">
              <svg width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
                <path d="m28.512 15.15-17.987-11A1 1 0 0 0 9 5v22a1 1 0 0 0 1.525.85l17.987-11a.987.987 0 0 0 0-1.7v0Z" stroke="#FFFFFF" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="rounded-full text-while absolute h-12 w-12 bg-primary z-50 items-center justify-center hidden [.is-pausing_&]:flex">
              <svg width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
                <g stroke="#FFFFFF" stroke-linecap="round" stroke-linejoin="round"><path d="M25 5h-4.5a1 1 0 0 0-1 1v20a1 1 0 0 0 1 1H25a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1ZM11.5 5H7a1 1 0 0 0-1 1v20a1 1 0 0 0 1 1h4.5a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1Z"/>
                </g>
              </svg>
            </div>
          </div>`
        : '<div class="absolute left-0 top-0 w-full h-full z-5 flex justify-center items-center cursor-pointer js-video-toggle"></div>'

    this.insertAdjacentHTML('beforeend', html)

    const buttonVideoToggle = this.querySelector('.js-video-toggle')

    if (buttonVideoToggle) {
      buttonVideoToggle.addEventListener('click', () => {
        if (!this.player) {
          throw new Error('player is undefined')
        }

        if (this.status.play) {
          this.onPause()
        } else {
          this.onPlay()
        }
      })
    }
  }

  videoMuteToggle(player) {
    const muteButton = this.querySelectorAll(selectorsVideo.MUTE_BUTTON_SELECTOR);
    const unmuteButton = this.querySelectorAll(selectorsVideo.UNMUTE_BUTTON_SELECTOR);

    if (player && muteButton && unmuteButton) {
      muteButton.forEach((button) => {
        button?.addEventListener('click', () => {
          if (this.type === selectorsVideo.VIDEO_TYPES.YOUTUBE) {
            player.mute()
          }

          if (this.type === selectorsVideo.VIDEO_TYPES.VIMEO) {
            player.setVolume(0)
          }

          if (this.type === selectorsVideo.VIDEO_TYPES.MP4) {
            player.muted = true
          }

            button.classList.add('active')
            unmuteButton.forEach((unButton) => {
              unButton.classList.remove('active')
            })
        });
      })

      unmuteButton.forEach((button) => {
        button?.addEventListener('click', () => {
          if (this.type === selectorsVideo.VIDEO_TYPES.YOUTUBE) {
            player.unMute()
          }

          if (this.type === selectorsVideo.VIDEO_TYPES.VIMEO) {
            player.setVolume(1)
          }

          if (this.type === selectorsVideo.VIDEO_TYPES.MP4) {
            player.muted = false
          }

          button.classList.add('active')
          muteButton.forEach((muteButton) => {
            muteButton.classList.remove('active')
          })
        })
      })
    }
  }

  async init() {
    this.type !== selectorsVideo.VIDEO_TYPES.MP4 && (await this.loadPlayerAPIs(this.type))

    if (window.theme.pageType === 'index') {
      let count = 0
      const bodyEl = document.querySelector('body')

      const interval = setInterval(() => {
        count++

        if (count > selectorsVideo.LIMIT_THRESHOLD) {
          console.warn('limited interval loaded')
          clearInterval(interval)
        }

        if (!bodyEl?.classList.contains('is-loading')) {
          this.initByType(this.type)
          clearInterval(interval)
        }
      }, selectorsVideo.INTERVAL_TIME_LOADING);
      
    } else {
      this.initByType(this.type)
    }
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer)
}
